import { useState } from 'react'
import { usePersonas, useCreatePersona, useUpdatePersona, useDeletePersona, useSetDefaultPersona } from '../hooks/usePersonas'
import { PersonaCard } from './PersonaCard'
import { PersonaForm } from './PersonaForm'
import { DeletePersonaModal } from './DeletePersonaModal'
import type { Persona, PersonaFormData } from '../types'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

const MAX_PERSONAS = 5

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-2xl border border-surface-200 bg-white p-5">
      <div className="flex gap-3 mb-4">
        <div className="h-11 w-11 rounded-full bg-surface-200" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 rounded bg-surface-200" />
          <div className="h-3 w-48 rounded bg-surface-100" />
        </div>
      </div>
      <div className="flex gap-1.5 mb-4">
        <div className="h-5 w-20 rounded-full bg-surface-200" />
        <div className="h-5 w-24 rounded-full bg-surface-100" />
      </div>
      <div className="flex gap-2 pt-3 border-t border-surface-100">
        <div className="h-11 flex-1 rounded-lg bg-surface-100" />
        <div className="h-11 flex-1 rounded-lg bg-surface-100" />
      </div>
    </div>
  )
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-surface-200 py-14 px-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-mindeia-primary-light">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <circle cx="16" cy="12" r="5" stroke="#1B6FE4" strokeWidth="2"/>
          <path d="M6 26c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="#1B6FE4" strokeWidth="2" strokeLinecap="round"/>
          <path d="M22 8h6M25 5v6" stroke="#1B6FE4" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
      <div>
        <p className="font-semibold text-neutral-900">{t('personas.empty_title')}</p>
        <p className="text-sm text-neutral-600 mt-1">{t('personas.empty_hint')}</p>
      </div>
      <button
        type="button"
        onClick={onCreate}
        className="min-h-[44px] rounded-lg bg-mindeia-primary px-6 text-sm font-semibold text-white transition-colors hover:bg-mindeia-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary"
      >
        {t('personas.create')}
      </button>
    </div>
  )
}

export function PersonaList() {
  const { t } = useTranslation()
  const { data: personas, isLoading, isError } = usePersonas()
  const { mutate: create, isPending: isCreating } = useCreatePersona()
  const { mutate: update, isPending: isUpdating } = useUpdatePersona()
  const { mutate: remove, isPending: isDeleting } = useDeletePersona()
  const { mutate: setDefault, isPending: isSettingDefault } = useSetDefaultPersona()

  const [showForm, setShowForm] = useState(false)
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null)
  const [deletingPersona, setDeletingPersona] = useState<Persona | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const quota = personas?.length ?? 0
  const isQuotaFull = quota >= MAX_PERSONAS

  const handleCreate = (data: PersonaFormData) => {
    setFormError(null)
    create(data, {
      onSuccess: () => setShowForm(false),
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          setFormError(err.response?.data?.error?.message ?? t('personas.create_error'))
        }
      },
    })
  }

  const handleUpdate = (data: PersonaFormData) => {
    if (!editingPersona) return
    setFormError(null)
    update({ id: editingPersona.id, data }, {
      onSuccess: () => setEditingPersona(null),
      onError: () => setFormError(t('personas.update_error')),
    })
  }

  const handleDelete = () => {
    if (!deletingPersona) return
    remove(deletingPersona.id, {
      onSuccess: () => setDeletingPersona(null),
    })
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-mindeia-error-light bg-mindeia-error-light p-4 text-sm text-mindeia-error">
        {t('personas.load_error')}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">{t('personas.list_title')}</h2>
          <p className="text-sm text-neutral-600">
            {t('personas.quota', { used: quota, max: MAX_PERSONAS })}
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => { setShowForm(true); setEditingPersona(null); setFormError(null) }}
            disabled={isQuotaFull}
            title={isQuotaFull ? t('personas.quota_full', { max: MAX_PERSONAS }) : undefined}
            className="flex min-h-[44px] items-center gap-2 rounded-lg bg-mindeia-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-mindeia-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {t('personas.new')}
          </button>
        </div>
      </div>

      {/* Form inline */}
      {(showForm || editingPersona) && (
        <div className="rounded-2xl border border-mindeia-primary-light bg-white p-6">
          <h3 className="text-base font-semibold text-neutral-900 mb-4">
            {editingPersona ? t('personas.edit') : t('personas.new')}
          </h3>
          <PersonaForm
            persona={editingPersona ?? undefined}
            isPending={isCreating || isUpdating}
            error={formError}
            onSubmit={editingPersona ? handleUpdate : handleCreate}
            onCancel={() => { setShowForm(false); setEditingPersona(null); setFormError(null) }}
          />
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : personas && personas.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {personas.map((persona) => (
            <PersonaCard
              key={persona.id}
              persona={persona}
              onEdit={(p) => { setEditingPersona(p); setShowForm(false); setFormError(null) }}
              onDelete={(p) => setDeletingPersona(p)}
              onSetDefault={(id) => setDefault(id)}
              isSettingDefault={isSettingDefault}
            />
          ))}
        </div>
      ) : !showForm ? (
        <EmptyState onCreate={() => setShowForm(true)} />
      ) : null}

      {/* Delete modal */}
      {deletingPersona && (
        <DeletePersonaModal
          persona={deletingPersona}
          isPending={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setDeletingPersona(null)}
        />
      )}
    </div>
  )
}
